import ChariotConnect from 'react-chariot-connect';
import * as dafGivingHubService from './api/dafGivingHubService';
import { FieldError } from '../cart-review/AlertMessage';
import { LoadingEllipsis } from '../common/LoadingEllipsis';
import React from 'react';
import PropTypes from 'prop-types';
import { saveCheckPledge } from './api/saveCheckPledge';
import { StaticUserInfo } from './StaticUserInfo';
import { isEmailAddress } from '../utils/emailUtils.ts';

/**
 * The main form used in the "DAF Giving Hub" feature. Whether it actually wraps Chariot's code or not depends on
 * feature flags.
 */
export default function ChariotGivingWrapper({
    connectId, loggedInUser, useChariot, hasExistingUser,
}) {
    const API_ERROR_MESSAGE = <span>
        {'Please refresh and try again. If you run into issues again, '}
        <a href="https://secure.donorschoose.org/help/donor_contact_form.html" target="_blank"
            rel="noopener noreferrer">
            contact customer support
        </a>.</span>;

    const [email, setEmail] = React.useState(loggedInUser != null ? loggedInUser.userName : '');
    const [fullName, setFullName] = React.useState(loggedInUser != null ? (loggedInUser.firstName + ' ' + loggedInUser.lastName) : '');
    const [amount, setAmount] = React.useState('');
    const [isCustomAmount, setIsCustomAmount] = React.useState(false);
    const [validEmail, setValidEmail] = React.useState(true);
    const [emailValidationError, setEmailValidationError] = React.useState('');
    const [validFullName, setValidFullName] = React.useState(true);
    const [fullNameValidationError, setFullNameValidationError] = React.useState('');
    const [validAmount, setValidAmount] = React.useState(true);
    const [amountValidationError, setAmountValidationError] = React.useState('');
    const [validApiRequest, setValidApiRequest] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = React.useState(false);

    function redirectToThankYouPage(content) {
        dafGivingHubService.saveThankYouDetails(content)
            .done(response => {
                if (response.success) {
                    window.location = secureBaseURL + 'donors/daf-giving-hub-thank-you.html?id=' + content.id;
                }
            })
            .fail(() => {
                setValidApiRequest(false);
            });
    }

    function onSuccess(r) {
        setIsLoading(true);
        setDisableSubmitButton(false);
        console.log('CHARIOT_SUCCESS');
        if (r) {
            const detail = r.detail;
            if (detail.grantIntent && detail.workflowSessionId) {
                detail.grantIntent.email = email;
                detail.grantIntent.fullName = fullName;

                console.log('Calling create grant...');

                dcAnalytics.track('DAFpay Integrated Success', {
                    preGrantAmount: amount,
                    finalAmount: detail.grantIntent.amount / 100,
                    upsold: (detail.grantIntent.amount / 100) > amount,
                });
                // Checking if the user was upsold by Chariot and persisting the preGrantAmount
                detail.grantIntent.preGrantAmount = amount;

                dafGivingHubService.createGrant(detail.grantIntent, detail.workflowSessionId)
                    .done(response => {
                        if (response.success) {
                            console.log('create grant successful... redirecting to thank you page');
                            redirectToThankYouPage({ id: response.data });
                        }
                    })
                    .fail(() => {
                        setIsLoading(false);
                        setValidApiRequest(false);
                    });
            }
        } else {
            console.log('Missing response from Chariot');
            setIsLoading(false);
            setValidApiRequest(false);
        }
    }

    function onExit(e) {
        setDisableSubmitButton(false);
        console.log('CHARIOT_EXIT');
        let grantDebugInfo = {
            exitReason: e.detail.reason,
            fullName,
            email,
            amount,
        };
        if (e) {
            if (e.detail.reason === 'UNINTEGRATED_GRANT_CONFIRMED') {
                setIsLoading(true);
                const unintegratedGrant = e.detail.unintegratedGrant;

                console.log('calling save unintegrated grant...');
                grantDebugInfo = {
                    ...grantDebugInfo,
                    amount: e.detail.unintegratedGrant.amount / 100,
                };
                dafGivingHubService.saveUnintegratedGrant(unintegratedGrant)
                    .done(response => {
                        if (response.success) {
                            console.log('create unintegrated grant successful... redirecting to thank you page');
                            redirectToThankYouPage({
                                id: response.data,
                                fundName: unintegratedGrant.fund.name,
                                redirectUrl: unintegratedGrant.fund.redirectUrl,
                                trackingId: unintegratedGrant.trackingId,
                            });
                        }
                    })
                    .fail(() => {
                        console.log('create unintegrated grant failure...');
                        setIsLoading(false);
                        setValidApiRequest(false);
                    });
            } else {
                setIsLoading(false);
            }
            dafGivingHubService.sendExitReason(grantDebugInfo, e.detail.workflowSessionId);
        }
    }

    function setFieldValidations(field, isValid, message) {
        switch (field) {
        case 'email':
            setValidEmail(isValid);
            setEmailValidationError(message);
            break;
        case 'fullName':
            setValidFullName(isValid);
            setFullNameValidationError(message);
            break;
        case 'amount':
            setValidAmount(isValid);
            setAmountValidationError(message);
            break;
        default:
            break;
        }
    }

    function resetFieldValidations() {
        setValidEmail(true);
        setEmailValidationError('');
        setValidFullName(true);
        setFullNameValidationError('');
        setValidAmount(true);
        setAmountValidationError('');
        setValidApiRequest(true);
        setDisableSubmitButton(true);
    }

    function validateFullName(name) {
        return !(!name || name.trim().split(/\s+/).length < 2);
    }

    function validateFields() {
        let isValid = true;

        if (!isEmailAddress(email)) {
            setFieldValidations('email', false, 'Please enter a valid email address');
            isValid = false;
        } else {
            setFieldValidations('email', true, '');
        }

        if (!validateFullName(fullName)) {
            setFieldValidations('fullName', false, 'Please enter your full name');
            isValid = false;
        } else {
            setFieldValidations('fullName', true, '');
        }

        if (amount <= 0) {
            setFieldValidations('amount', false, 'Please select a donation amount');
            isValid = false;
        } else {
            setFieldValidations('amount', true, '');
        }

        return isValid;
    }

    function onDonationRequest() {
        resetFieldValidations();
        if (validateFields()) {
            return {
                amount: amount * 100,
                firstName: fullName.split(' ').slice(0, -1).join(' '),
                lastName: fullName.split(' ').slice(-1)[0],
                email,
            };
        } else {
            setDisableSubmitButton(false);
            return false;
        }
    }

    function onCheckPledgeSubmit(e) {
        e.preventDefault();

        resetFieldValidations();
        if (validateFields()) {
            setIsLoading(true);

            saveCheckPledge({ name: fullName, amount, email })
                .then((response) => {
                    setIsLoading(false);
                    if (response.success) {
                        window.location = `${secureBaseURL}donors/checkPaymentInstructions.html?id=${response.data}`;
                    }
                }).fail(() => {
                    setIsLoading(false);
                    setValidApiRequest(false);
                });
        }
    }

    function renderButtonOptions() {
        const paymentOptions = [
            { label: '$1,000', value: 1000 },
            { label: '$2,500', value: 2500 },
            { label: '$5,000', value: 5000 },
            { label: '$10,000', value: 10000 },
        ];
        return (
            <div className="payment-select">
                {paymentOptions.map((option, idx) => (
                    <button
                        key={option.value}
                        type="button"
                        className={`select-amount-button${amount === option.value && !isCustomAmount ? ' selected' : ''}`}
                        onClick={() => {
                            setAmount(option.value);
                            setIsCustomAmount(false);
                        }}>

                        {option.label}
                    </button>
                ))}
            </div>);
    }

    function renderCustomAmountLabel() {
        return (
            <label
                className="custom-amount"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    setAmount('');
                    setIsCustomAmount(true);
                }}>
                Enter custom amount
            </label>);
    }

    function renderCustomAmountButton() {
        return (
            <div className="custom-amount-input-container">
                <span>$</span>
                <input
                    type="number"
                    value={amount}
                    className="custom-amount-input"
                    placeholder="Enter custom amount"
                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                    onChange={e => setAmount(e.target.value)}
                />
            </div>);
    }

    function renderCustomAmountInput() {
        return (<div className="custom-amount-container form-redux">
            {isCustomAmount ? renderCustomAmountButton() : renderCustomAmountLabel()}
        </div>);
    }

    function renderPaymentOptions() {
        return (
            <div className="payment-options">
                {renderButtonOptions()}
                {renderCustomAmountInput()}
                {!validAmount ? (<FieldError content={amountValidationError}/>) : null}
            </div>
        );
    }

    function renderNameFields() {
        // In the Chariot flow, we let you edit name/email even if you're signed in (partly because we can't guarantee
        // your Chariot user info matches your DonorsChoose user info). If Chariot isn't involved, we don't have to deal
        // with that possibility.
        if (hasExistingUser && !useChariot) {
            return <StaticUserInfo fullName={fullName} email={email} />;
        }

        return (
            <div className="userInfoContainer form-redux">
                <div className="name-field">
                    <label htmlFor="fullName">Full Name*</label>
                    <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        defaultValue={fullName}
                        maxLength={50}
                        onChange={e => setFullName(e.target.value.trim())}
                    />
                    {!validFullName ? (<FieldError content={fullNameValidationError}/>) : null}
                </div>
                <div className="email-field">
                    <label htmlFor="email">Email*</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        defaultValue={email}
                        onChange={e => setEmail(e.target.value.trim())}
                    />
                    {!validEmail ? (<FieldError content={emailValidationError}/>) : null}
                </div>
            </div>
        );
    }

    function renderContinueButton() {
        let button;
        if (useChariot) {
            button = (
                <ChariotConnect
                    cid={connectId}
                    onDonationRequest={onDonationRequest}
                    onExit={onExit}
                    disabled={disableSubmitButton}
                    onSuccess={onSuccess}>
                </ChariotConnect>
            );
        } else {
            button = <button className="button-primary" onClick={onCheckPledgeSubmit}>Place my donation</button>;
        }

        return (
            <div className="form-redux">
                {button}
                {!validApiRequest ? (<FieldError content={API_ERROR_MESSAGE}/>) : null}
            </div>
        );
    }

    function renderLoadingElipsis() {
        return (
            <LoadingEllipsis/>
        );
    }

    function renderFormFieldsAndButton() {
        return (
            <div className="DAF-form">
                {renderPaymentOptions()}
                {renderNameFields()}
                {renderContinueButton()}
                {useChariot && <a href="#dafpay-faq">What is DAFpay?</a> }
            </div>
        );
    }

    return (
        <div>
            {isLoading ? renderLoadingElipsis() : renderFormFieldsAndButton()}
        </div>
    );
}

ChariotGivingWrapper.propTypes = {
    connectId: PropTypes.string.isRequired,
    loggedInUser: PropTypes.object,
    useChariot: PropTypes.bool,
    hasExistingUser: PropTypes.bool.isRequired,
};
